import type { JSX } from 'react';
import type { Key, ReactNode } from 'react';
import { Table } from 'antd';
import type { TableColumnsType, TablePaginationConfig } from 'antd';

import type { CsvColumn } from './export-csv';

type Cell = string | number | boolean | null | undefined;

export interface DataColumn<T> {
  key: string;
  title: ReactNode;
  /** Значение ячейки для сортировки, CSV и показа по умолчанию. */
  value?: (row: T) => Cell;
  /** Своя отрисовка (иначе — `value`). */
  render?: (row: T, index: number) => ReactNode;
  /** Число: выравнивание вправо, tabular-nums, сортировка по убыванию первым кликом. */
  num?: boolean;
  /** false — колонку не сортировать (по умолчанию сортируется, если есть `value`). */
  sortable?: boolean;
  /** Значение в строке итогов (уже отформатированное). */
  total?: ReactNode;
  /** Заголовок колонки для CSV (если title — не строка). */
  csvTitle?: string;
  /** Не выгружать в CSV (кнопки действий). */
  noCsv?: boolean;
  width?: number | string;
  ellipsis?: boolean;
  fixed?: 'left' | 'right';
}

export interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => Key;
  /** Сортировка при открытии. */
  defaultSort?: { key: string; order: 'ascend' | 'descend' };
  /** Показать строку «Итого» (значения — `column.total`). */
  totals?: boolean;
  totalLabel?: string;
  loading?: boolean;
  /** compact — 32 px строки (плотные отчёты), regular — 40 px. */
  density?: 'regular' | 'compact';
  /** Размер страницы; false — без пагинации. По умолчанию 50, если строк больше. */
  pageSize?: number | false;
  onRowClick?: (row: T) => void;
  emptyText?: ReactNode;
}

function compare(a: Cell, b: Cell): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ru', { numeric: true });
}

/**
 * Таблица отчёта HUB 2.0 (порт `_ds/components/DataTable` поверх antd Table):
 * сортировка по клику (активная колонка — оранжевым), тонкие линии, строки
 * 40 px, строка итогов, горизонтальный скролл широких таблиц. Пагинация по
 * правилу CLAUDE.md: `defaultPageSize` + `showSizeChanger`.
 */
export function DataTable<T extends object>({
  columns,
  rows,
  rowKey,
  defaultSort,
  totals,
  totalLabel = 'Итого',
  loading,
  density = 'regular',
  pageSize,
  onRowClick,
  emptyText,
}: DataTableProps<T>): JSX.Element {
  const antCols: TableColumnsType<T> = columns.map((c) => {
    const get = c.value;
    const canSort = c.sortable !== false && !!get;
    return {
      key: c.key,
      title: c.title,
      align: c.num ? 'right' : 'left',
      width: c.width,
      ellipsis: c.ellipsis,
      fixed: c.fixed,
      sorter: canSort ? (a: T, b: T) => compare(get(a), get(b)) : undefined,
      sortDirections: c.num ? ['descend', 'ascend'] : ['ascend', 'descend'],
      defaultSortOrder: defaultSort?.key === c.key ? defaultSort.order : undefined,
      render: (_: unknown, row: T, i: number) => (c.render ? c.render(row, i) : (get?.(row) as ReactNode) ?? '—'),
    };
  });
  const size = pageSize === undefined ? 50 : pageSize;
  const pagination: TablePaginationConfig | false =
    size === false || rows.length <= size ? false : { defaultPageSize: size, showSizeChanger: true };

  return (
    <div className={`fk-ds-table${density === 'compact' ? ' fk-ds-table--compact' : ''}`}>
      <Table<T>
        columns={antCols}
        dataSource={rows as T[]}
        rowKey={(r) => rowKey(r)}
        loading={loading}
        pagination={pagination}
        size="middle"
        scroll={{ x: 'max-content' }}
        showSorterTooltip={false}
        locale={emptyText ? { emptyText } : undefined}
        onRow={onRowClick ? (r) => ({ onClick: () => onRowClick(r), style: { cursor: 'pointer' } }) : undefined}
        summary={
          totals
            ? () => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    {columns.map((c, i) => (
                      <Table.Summary.Cell key={c.key} index={i} align={c.num ? 'right' : 'left'}>
                        {i === 0 && c.total == null ? totalLabel : c.total ?? ''}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            : undefined
        }
      />
    </div>
  );
}

/** Колонки таблицы → колонки CSV (для ExportMenu: «что на экране»). */
export function dataTableCsvColumns<T>(columns: DataColumn<T>[]): CsvColumn<T>[] {
  return columns
    .filter((c) => !c.noCsv && c.value)
    .map((c) => ({
      title: c.csvTitle ?? (typeof c.title === 'string' ? c.title : c.key),
      value: (r: T) => c.value!(r),
    }));
}
